import { useState } from "react";
import {
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Divider,
  Alert,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { LoginContainer, LoginCard, LogoBox, Form } from "./Login.styled";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "../config/firebase.config.js";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";

const Login = () => {
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError("");
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    // Basic validation
    if (!formData.email || !formData.password) {
      setError("Please fill in all fields");
      return;
    }
    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      setError("Please enter a valid email address");
      return;
    }
    if (formData.password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    try {
      setLoading(true);
      await signInWithEmailAndPassword(auth, formData.email, formData.password);
      toast.success("Login successful! Welcome back.");
      navigate("/admin/users");
      setFormData({ email: "", password: "" });
    } catch (err) {
      console.error("Login error:", err);
      setError(`Login failed. ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoginContainer>
      <LoginCard>
        {/* Logo + Title */}
        <LogoBox>
          <Typography
            sx={{
              fontSize: "var(--text-xxl)",
              fontWeight: 800,
              color: "var(--btn-purple-color)",
              letterSpacing: "-1px",
            }}
          >
            CourseCut
          </Typography>
          <Typography
            sx={{
              fontSize: "var(--text-sm)",
              color: "var(--gray-light)",
            }}
          >
            MIS Dashboard
          </Typography>
        </LogoBox>

        <Divider sx={{ marginBottom: "var(--space-xl)" }} />

        {/* Heading */}
        <Typography
          sx={{
            fontSize: "var(--text-xl)",
            fontWeight: 700,
            color: "var(--text-color-primary)",
            marginBottom: "var(--space-xs)",
          }}
        >
          Welcome back 👋
        </Typography>
        <Typography
          sx={{
            fontSize: "var(--text-sm)",
            color: "var(--gray-light)",
            marginBottom: "var(--space-lg)",
          }}
        >
          Sign in to your admin account
        </Typography>

        {/* Error Alert */}
        {error && (
          <Alert
            severity="error"
            sx={{
              marginBottom: "var(--space-md)",
              borderRadius: "var(--radius-md)",
              fontSize: "var(--text-sm)",
            }}
          >
            {error}
          </Alert>
        )}

        <Form onSubmit={handleLogin}>
          {/* Email */}
          <TextField
            label="Email Address"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="support@coursecut.ai"
            fullWidth
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlinedIcon
                      sx={{
                        fontSize: "20px",
                        color: "var(--theme-purple-color)",
                      }}
                    />
                  </InputAdornment>
                ),
              },
            }}
          />

          {/* Password */}
          <TextField
            label="Password"
            name="password"
            type={showPassword ? "text" : "password"}
            value={formData.password}
            onChange={handleChange}
            placeholder="Enter your password"
            fullWidth
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon
                      sx={{
                        fontSize: "20px",
                        color: "var(--theme-purple-color)",
                      }}
                    />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((prev) => !prev)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? (
                        <VisibilityOffIcon
                          sx={{
                            fontSize: "20px",
                            color: "var(--theme-purple-color)",
                          }}
                        />
                      ) : (
                        <VisibilityIcon
                          sx={{
                            fontSize: "20px",
                            color: "var(--theme-purple-color)",
                          }}
                        />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />

          {/* Submit */}
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading || !formData.email || !formData.password}
            sx={{
              marginTop: "var(--space-sm)",
              padding: "var(--space-sm) var(--space-lg)",
              fontSize: "var(--text-base)",
              fontWeight: 600,
              borderRadius: "var(--radius-md)",
              backgroundColor: "var(--btn-purple-color)",
              "&:hover": { backgroundColor: "var(--btn-purple-hover)" },
              "&:active": { backgroundColor: "var(--btn-purple-active)" },
            }}
          >
            {loading ? "Signing in..." : "Sign In"}
          </Button>
        </Form>
      </LoginCard>
    </LoginContainer>
  );
};

export default Login;
